import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export const useSharedSupabaseData = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Goals
  const [goals, setGoals] = useState([]);
  
  // Text items
  const [realMotives, setRealMotives] = useState([]);
  const [dailyNotes, setDailyNotes] = useState([]);

  // Load all data when component mounts
  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadGoals(),
        loadRealMotives(),
        loadDailyNotes()
      ]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Goals functions
  const loadGoals = async () => {
    const { data, error } = await supabase
      .from('goals_shared_ms2025')
      .select('*')
      .order('created_at', { ascending: true });
    
    if (error) throw error;
    setGoals(data || []);
  };

  const addGoal = async (text, type) => {
    const { data, error } = await supabase
      .from('goals_shared_ms2025')
      .insert([{ text, type }])
      .select();
    
    if (error) throw error;
    setGoals(prev => [...prev, ...data]);
  };

  const updateGoal = async (id, updates) => {
    const { data, error } = await supabase
      .from('goals_shared_ms2025')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select();
    
    if (error) throw error;
    setGoals(prev => prev.map(goal => goal.id === id ? data[0] : goal));
  };

  const deleteGoal = async (id) => {
    const { error } = await supabase
      .from('goals_shared_ms2025')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
    setGoals(prev => prev.filter(goal => goal.id !== id));
  };

  // Real motives functions
  const loadRealMotives = async () => {
    const { data, error } = await supabase
      .from('real_motives_shared_ms2025')
      .select('*')
      .order('created_at', { ascending: true });
    
    if (error) throw error;
    setRealMotives(data || []);
  };

  const addRealMotive = async (text) => {
    const { data, error } = await supabase
      .from('real_motives_shared_ms2025')
      .insert([{ text }])
      .select();
    
    if (error) throw error;
    setRealMotives(prev => [...prev, ...data]);
  };

  const deleteRealMotive = async (id) => {
    const { error } = await supabase
      .from('real_motives_shared_ms2025')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
    setRealMotives(prev => prev.filter(motive => motive.id !== id));
  };

  // Daily notes functions
  const loadDailyNotes = async () => {
    const today = new Date().toISOString().split('T')[0];
    const { data, error } = await supabase
      .from('daily_notes_shared_ms2025')
      .select('*')
      .eq('note_date', today)
      .order('created_at', { ascending: true });
    
    if (error) throw error;
    setDailyNotes(data || []);
  };

  const addDailyNote = async (text) => {
    const today = new Date().toISOString().split('T')[0];
    const { data, error } = await supabase
      .from('daily_notes_shared_ms2025')
      .insert([{ text, note_date: today }])
      .select();
    
    if (error) throw error;
    setDailyNotes(prev => [...prev, ...data]);
  };

  const updateDailyNote = async (id, updates) => {
    const { data, error } = await supabase
      .from('daily_notes_shared_ms2025')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select();
    
    if (error) throw error;
    setDailyNotes(prev => prev.map(note => note.id === id ? data[0] : note));
  };

  const deleteDailyNote = async (id) => {
    const { error } = await supabase
      .from('daily_notes_shared_ms2025')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
    setDailyNotes(prev => prev.filter(note => note.id !== id));
  };

  return {
    loading,
    error,
    
    // Goals
    goals,
    addGoal,
    updateGoal,
    deleteGoal,
    
    // Text items
    realMotives,
    addRealMotive,
    deleteRealMotive,
    dailyNotes,
    addDailyNote,
    updateDailyNote,
    deleteDailyNote,
    
    // Utility
    loadAllData
  };
};